import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, Calendar, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookSlot() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tokenCode = searchParams.get('t') || '';
  const prefillName = searchParams.get('n') || '';
  const prefillPhone = searchParams.get('p') || '';
  const prefillEmail = searchParams.get('e') || '';

  const [event, setEvent] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({ name: prefillName, phone: prefillPhone, email: prefillEmail });
  const [hasPrefill, setHasPrefill] = useState(!!(prefillName || prefillPhone || prefillEmail));
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookedDate, setBookedDate] = useState('');
  const [bookedTime, setBookedTime] = useState('');
  const [error, setError] = useState('');

  const fetchSlots = async () => {
    try {
      const r = await fetch(`${API_URL}/api/events/${slug}/slots`);
      if (r.ok) {
        const data = await r.json();
        setEvent(data.event);
        setSlots(data.slots);
      }
    } catch {}
  };

  useEffect(() => {
    fetch(`${API_URL}/api/events/${slug}/slots`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setEvent(data.event); setSlots(data.slots); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
    if (tokenCode && !hasPrefill) {
      fetch(`${API_URL}/api/tokens/${tokenCode}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) { setFormData({ name: data.name, phone: data.phone, email: data.email }); setHasPrefill(true); } });
    }
  }, [slug, tokenCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/events/${slug}/slots/${selectedSlot}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.status === 409) {
        setError(data.detail || 'This slot was just booked by someone else. Please pick another.');
        setSelectedSlot(null);
        await fetchSlots();
      } else if (data.success) {
        setSuccess(true);
        setBookedDate(data.slot_date);
        setBookedTime(data.slot_time);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><p className="text-white">Loading...</p></div>;
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Not Found</h1>
          <p className="text-gray-400">This booking link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#F5A623]" />
            </div>
          </div>
          <h1 data-testid="booking-success-title" className="text-4xl font-black text-white mb-4">You're Booked!</h1>
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6 mb-4 text-left">
            <p className="text-[#F5A623] text-xs uppercase tracking-widest font-bold mb-3">Your Appointment</p>
            <p className="text-white font-bold text-lg">{event?.name}</p>
            <div className="flex items-center gap-4 mt-2 text-gray-300">
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#F5A623]" /> {bookedDate}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#F5A623]" /> {bookedTime}</span>
            </div>
          </div>
          <p className="text-gray-400 text-sm">Our team will reach out to confirm the details. Thank you!</p>
        </div>
      </div>
    );
  }

  // Group slots by date
  const grouped = {};
  slots.forEach(s => {
    if (!grouped[s.day_label]) grouped[s.day_label] = [];
    grouped[s.day_label].push(s);
  });

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="bg-[#1A1A1D] border border-white/10 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#F5A623] to-[#E59400] p-6 text-center">
            <h1 className="text-2xl sm:text-3xl font-black text-black mb-1">{event?.name}</h1>
            <p className="text-black/70 text-sm">Pick your preferred time slot</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Slot Selection */}
            {Object.keys(grouped).length === 0 ? (
              <p className="text-gray-400 text-center py-6">No available slots at the moment. Please check back later.</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(grouped).map(([dayLabel, daySlots]) => (
                  <div key={dayLabel}>
                    <p className="text-sm font-bold text-gray-300 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#F5A623]" /> {dayLabel}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {daySlots.map(slot => {
                        // Generate a consistent pseudo-random viewer count per slot
                        const hash = slot.id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
                        const viewers = (hash % 3) + 1; // 1, 2, or 3
                        return (
                        <div key={slot.id} className="relative">
                          <button
                            type="button"
                            data-testid={`slot-${slot.id}`}
                            onClick={() => setSelectedSlot(slot.id)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                              selectedSlot === slot.id
                                ? 'bg-[#F5A623] text-black ring-2 ring-[#F5A623]/50'
                                : 'bg-[#0A0A0B] text-gray-300 border border-white/10 hover:border-[#F5A623]/50'
                            }`}
                          >
                            <Clock className="w-3.5 h-3.5" /> {slot.time}
                          </button>
                          <span className="block text-[10px] text-orange-400/80 mt-0.5 text-center">{viewers} {viewers === 1 ? 'person' : 'people'} viewing</span>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Contact Form (shown after selecting slot) */}
            {selectedSlot && (
              <div className="space-y-4 pt-2 border-t border-white/10">
                {hasPrefill ? (
                  <p className="text-sm text-gray-400">Booking as <span className="text-white font-medium">{formData.name}</span></p>
                ) : (
                  <>
                    <p className="text-sm text-gray-400">Fill in your details to confirm:</p>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Full Name *</Label>
                      <Input data-testid="book-name-input" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="Enter your full name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Mobile Number *</Label>
                      <Input data-testid="book-phone-input" type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="+60 12 345 6789" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Email Address *</Label>
                      <Input data-testid="book-email-input" type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="your.email@example.com" />
                    </div>
                  </>
                )}

                {error && <p data-testid="booking-error" className="text-red-400 text-sm">{error}</p>}

                <Button
                  data-testid="book-confirm-btn"
                  type="submit" disabled={submitting}
                  className="w-full bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-12 text-lg"
                >
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            )}
          </form>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">Signature of Wealth by Steven Yong</p>
      </div>
    </div>
  );
}
